import type React from 'react';
import { useState } from 'react';
import { Phone, Plus, Trash2, Copy, Check, Pencil, Radio } from 'lucide-react';
import styled from '@emotion/styled';
import {
  PageHeader, PageTitle, Card, Table, Thead, Tbody,
  PrimaryButton, SecondaryButton, DestructiveButton,
  ModalOverlay, ModalDialog, ModalTitle, ModalActions,
  FormGroup, Input, Mono, Badge,
} from '../styled-components';
import { ConfirmModal, EmptyState, SkeletonRows } from '../components';
import { useFetchData, showToast } from '../hooks';
import { fetchOrThrow, getCSRFToken, CSRF_HEADER_NAME } from '../../common/utils';

function formatPhone(number: string): string {
  if (!number) return '—';
  const digits = number.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  if (digits.length === 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  return number;
}

function formatDate(s: string | null): string {
  if (!s) return '—';
  return new Date(s).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

// ---------------------------------------------------------------------------
// Copy button
// ---------------------------------------------------------------------------

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <button
      onClick={handleCopy}
      title="Copy"
      style={{
        background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px',
        color: copied ? 'var(--accent-green)' : 'var(--text-muted)',
        transition: 'color 150ms',
        display: 'inline-flex', alignItems: 'center',
      }}
    >
      {copied ? <Check size={14} /> : <Copy size={14} />}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Setup instructions callout
// ---------------------------------------------------------------------------

const SetupCard = styled.div({
  marginTop: 16,
  padding: '16px 20px',
  borderTop: '1px solid var(--bg-border)',
  fontSize: 13,
  lineHeight: 1.7,
  color: 'var(--text-secondary)',
  '& code': {
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: 12,
    color: 'var(--text-primary)',
    background: 'var(--bg-base)',
    padding: '1px 6px',
    borderRadius: 3,
  },
  '& p': { margin: '0 0 6px' },
  '& p:last-child': { margin: 0 },
});

// ---------------------------------------------------------------------------
// Phone Numbers page
// ---------------------------------------------------------------------------

interface AvailableNumber { phone_number: string }

type AddMode = 'buy' | 'custom';

const TabButton = styled.button<{ active: boolean }>(({ active }) => ({
  flex: 1,
  padding: '8px 0',
  fontSize: 13,
  fontWeight: 600,
  fontFamily: 'inherit',
  cursor: 'pointer',
  border: '1px solid var(--bg-border)',
  background: active ? 'var(--accent-cyan)' : 'transparent',
  color: active ? '#000' : 'var(--text-secondary)',
  transition: 'all 150ms',
  '&:first-of-type': { borderRadius: '6px 0 0 6px' },
  '&:last-of-type': { borderRadius: '0 6px 6px 0' },
}));

function AddNumberModal({ canAddCustom, onClose, onAdded }: { canAddCustom: boolean; onClose: () => void; onAdded: () => void }) {
  const [mode, setMode] = useState<AddMode>('buy');
  const [areaCode, setAreaCode] = useState('');
  const [results, setResults] = useState<AvailableNumber[]>([]);
  const [searching, setSearching] = useState(false);
  const [buying, setBuying] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);
  const [customNumber, setCustomNumber] = useState('');
  const [addingCustom, setAddingCustom] = useState(false);
  const [customError, setCustomError] = useState<string | null>(null);

  async function search() {
    setSearching(true);
    setSearched(true);
    try {
      const res = await fetch(`/dashboard/api/phone-numbers/available?area_code=${areaCode}`);
      const data = await res.json();
      setResults(data);
    } catch {
      showToast('Search failed', 'error');
    } finally {
      setSearching(false);
    }
  }

  async function buy(number: string) {
    setBuying(number);
    try {
      await fetchOrThrow('/dashboard/api/phone-numbers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', [CSRF_HEADER_NAME]: await getCSRFToken() },
        body: JSON.stringify({ phone_number: number }),
      });
      showToast(`${number} provisioned`, 'success');
      onAdded();
      onClose();
    } catch (e) {
      const msg = e instanceof Error && e.message.includes('409') ? 'Number no longer available' : 'Failed to provision number';
      showToast(msg, 'error');
    } finally {
      setBuying(null);
    }
  }

  async function addCustom() {
    if (!customNumber.trim()) return;
    setAddingCustom(true);
    setCustomError(null);
    try {
      await fetchOrThrow('/dashboard/api/phone-numbers/custom', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', [CSRF_HEADER_NAME]: await getCSRFToken() },
        body: JSON.stringify({ phone_number: customNumber }),
      });
      showToast('Phone number added', 'success');
      onAdded();
      onClose();
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to add number';
      setCustomError(msg.includes('409') ? 'This phone number has already been added.' : msg.includes('400') ? 'Invalid phone number. Please enter a valid US phone number.' : msg);
    } finally {
      setAddingCustom(false);
    }
  }

  return (
    <ModalOverlay onClick={onClose}>
      <ModalDialog onClick={(e) => e.stopPropagation()}>
        <ModalTitle>Add a Phone Number</ModalTitle>
        {canAddCustom && (
          <div style={{ display: 'flex', marginBottom: 16 }}>
            <TabButton active={mode === 'buy'} onClick={() => setMode('buy')}>Buy from Guava</TabButton>
            <TabButton active={mode === 'custom'} onClick={() => setMode('custom')}>Use Your Own</TabButton>
          </div>
        )}
        {mode === 'buy' ? (
          <>
            <FormGroup>
              <label>Area Code (optional)</label>
              <div style={{ display: 'flex', gap: 10 }}>
                <Input value={areaCode} onChange={(e) => setAreaCode(e.target.value)} placeholder="e.g. 415" style={{ flex: 1 }} />
                <PrimaryButton onClick={search} disabled={searching}>{searching ? 'Searching…' : 'Search'}</PrimaryButton>
              </div>
            </FormGroup>
            {searched && (
              <div style={{ maxHeight: 240, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
                {results.length === 0 && !searching && (
                  <div style={{ color: 'var(--text-muted)', fontSize: 13, textAlign: 'center', padding: 16 }}>
                    No numbers found. Try a different area code.
                  </div>
                )}
                {results.map((n) => (
                  <div key={n.phone_number} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '10px 12px', background: 'var(--bg-base)', borderRadius: 6,
                    border: '1px solid var(--bg-border)',
                  }}>
                    <Mono>{formatPhone(n.phone_number)}</Mono>
                    <PrimaryButton
                      style={{ padding: '5px 14px', fontSize: 13 }}
                      disabled={buying === n.phone_number}
                      onClick={() => buy(n.phone_number)}
                    >
                      {buying === n.phone_number ? 'Buying…' : 'Buy'}
                    </PrimaryButton>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <FormGroup>
            <label>Phone Number</label>
            <div style={{ display: 'flex', gap: 10 }}>
              <Input
                value={customNumber}
                onChange={(e) => { setCustomNumber(e.target.value); setCustomError(null); }}
                placeholder="e.g. +1 (415) 555-1234"
                style={{ flex: 1, borderColor: customError ? 'var(--accent-red)' : undefined }}
              />
              <PrimaryButton onClick={addCustom} disabled={addingCustom || !customNumber.trim()}>
                {addingCustom ? 'Adding…' : 'Add'}
              </PrimaryButton>
            </div>
            {customError && <div style={{ color: 'var(--accent-red)', fontSize: 13, marginTop: 6 }}>{customError}</div>}
            <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 8 }}>
              Custom numbers can only be used as caller ID for outbound dialing. They will not receive inbound calls.
            </div>
          </FormGroup>
        )}
        <ModalActions style={{ marginTop: 16 }}>
          <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
        </ModalActions>
      </ModalDialog>
    </ModalOverlay>
  );
}

interface PhoneNumber { id: string; phone_number: string; label: string; status: string; provisioned_at: string | null; active_session: boolean }

function EditLabelModal({ title, url, initialLabel, placeholder, onClose, onSaved }: {
  title: string; url: string; initialLabel: string; placeholder: string; onClose: () => void; onSaved: () => void;
}) {
  const [label, setLabel] = useState(initialLabel);
  const [saving, setSaving] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      await fetchOrThrow(url, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', [CSRF_HEADER_NAME]: await getCSRFToken() },
        body: JSON.stringify({ label }),
      });
      showToast('Label saved', 'success');
      onSaved(); onClose();
    } catch { showToast('Failed to save label', 'error'); }
    finally { setSaving(false); }
  }

  return (
    <ModalOverlay onClick={onClose}>
      <ModalDialog onClick={(e) => e.stopPropagation()}>
        <ModalTitle>Edit Label — {title}</ModalTitle>
        <form onSubmit={handleSubmit}>
          <FormGroup>
            <label>Label</label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder={placeholder} autoFocus />
          </FormGroup>
          <ModalActions>
            <SecondaryButton type="button" onClick={onClose}>Cancel</SecondaryButton>
            <PrimaryButton type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save'}</PrimaryButton>
          </ModalActions>
        </form>
      </ModalDialog>
    </ModalOverlay>
  );
}

export function PhoneNumbersPage() {
  const { data, loading, refetch } = useFetchData<PhoneNumber[]>('/dashboard/api/phone-numbers', []);
  const { data: canAddCustomData } = useFetchData<{ allowed: boolean }>('/dashboard/api/phone-numbers/can-add-custom', { allowed: false });
  const [showAdd, setShowAdd] = useState(false);
  const [confirmRelease, setConfirmRelease] = useState<PhoneNumber | null>(null);
  const [editLabel, setEditLabel] = useState<PhoneNumber | null>(null);
  const [releasing, setReleasing] = useState(false);

  async function handleRelease() {
    if (!confirmRelease) return;
    setReleasing(true);
    try {
      await fetchOrThrow(`/dashboard/api/phone-numbers/${confirmRelease.id}`, {
        method: 'DELETE', headers: { [CSRF_HEADER_NAME]: await getCSRFToken() },
      });
      showToast('Number released', 'success');
      refetch();
    } catch { showToast('Failed to release number', 'error'); }
    finally { setReleasing(false); setConfirmRelease(null); }
  }

  return (
    <div>
      <PageHeader>
        <PageTitle>Phone Numbers</PageTitle>
        <PrimaryButton onClick={() => setShowAdd(true)}><Plus size={16} /> Add Number</PrimaryButton>
      </PageHeader>

      <Card style={{ padding: 0, overflow: 'hidden' }}>
        <Table>
          <Thead><tr><th>Number</th><th>Label</th><th>Status</th><th>Provisioned</th><th></th></tr></Thead>
          <Tbody>
            {loading ? <SkeletonRows cols={5} /> : data.length === 0 ? (
              <tr><td colSpan={5}>
                <EmptyState icon={<Phone size={40} />} title="No phone numbers" body="Add a number to start making and receiving PSTN calls." action={{ label: 'Add Number', onClick: () => setShowAdd(true) }} />
              </td></tr>
            ) : data.map((n) => (
              <tr key={n.id}>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Mono>{formatPhone(n.phone_number)}</Mono>
                    <CopyButton value={n.phone_number} />
                  </div>
                </td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ color: 'var(--text-secondary)' }}>{n.label || '—'}</span>
                    <button
                      onClick={(e) => { e.stopPropagation(); setEditLabel(n); }}
                      title="Edit label"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', color: 'var(--text-muted)', display: 'inline-flex', alignItems: 'center' }}
                    >
                      <Pencil size={12} />
                    </button>
                  </div>
                </td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Badge variant={n.status}>{n.status}</Badge>
                    {n.active_session && (
                      <span title="Active call in progress" style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11, color: 'var(--accent-green)', fontWeight: 600 }}>
                        <Radio size={10} /> live
                      </span>
                    )}
                  </div>
                </td>
                <td style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{formatDate(n.provisioned_at)}</td>
                <td onClick={(e) => e.stopPropagation()}>
                  <DestructiveButton style={{ padding: '5px 10px', fontSize: 13 }} onClick={() => setConfirmRelease(n)}>
                    <Trash2 size={14} /> Release
                  </DestructiveButton>
                </td>
              </tr>
            ))}
          </Tbody>
        </Table>
        <SetupCard>
          <p>Phone numbers receive inbound PSTN calls and can be used as caller ID for outbound calls.</p>
          <p>In your call controller: <code>call_info.from_number</code> is the caller, <code>call_info.to_number</code> is the dialed number.</p>
        </SetupCard>
      </Card>

      {showAdd && <AddNumberModal canAddCustom={canAddCustomData.allowed} onClose={() => setShowAdd(false)} onAdded={refetch} />}
      {editLabel && (
        <EditLabelModal
          title={formatPhone(editLabel.phone_number)} url={`/dashboard/api/phone-numbers/${editLabel.id}`}
          initialLabel={editLabel.label} placeholder="e.g. Support Line"
          onClose={() => setEditLabel(null)} onSaved={refetch}
        />
      )}
      {confirmRelease && (
        <ConfirmModal
          title={`Release ${confirmRelease.phone_number}?`}
          body="This number will be released and stop receiving calls immediately."
          confirmLabel="Release" destructive loading={releasing}
          onConfirm={handleRelease} onCancel={() => setConfirmRelease(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// WebRTC Codes page
// ---------------------------------------------------------------------------

interface WebRTCCode { id: string; webrtc_code: string; label: string; created_at: string | null; expires_at: string | null; active_session: boolean }

function CreateWebRTCModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [ttl, setTtl] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      await fetchOrThrow('/dashboard/api/webrtc-codes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', [CSRF_HEADER_NAME]: await getCSRFToken() },
        body: JSON.stringify({ ttl_sec: ttl ? parseInt(ttl, 10) : null }),
      });
      showToast('WebRTC code created', 'success');
      onCreated(); onClose();
    } catch { showToast('Failed to create code', 'error'); }
    finally { setLoading(false); }
  }

  return (
    <ModalOverlay onClick={onClose}>
      <ModalDialog onClick={(e) => e.stopPropagation()}>
        <ModalTitle>Create WebRTC Code</ModalTitle>
        <form onSubmit={handleSubmit}>
          <FormGroup>
            <label>TTL (seconds, optional)</label>
            <Input type="number" value={ttl} onChange={(e) => setTtl(e.target.value)} placeholder="Leave blank for permanent" />
          </FormGroup>
          <ModalActions>
            <SecondaryButton type="button" onClick={onClose}>Cancel</SecondaryButton>
            <PrimaryButton type="submit" disabled={loading}>{loading ? 'Creating…' : 'Create'}</PrimaryButton>
          </ModalActions>
        </form>
      </ModalDialog>
    </ModalOverlay>
  );
}

export function WebRTCCodesPage() {
  const { data, loading, refetch } = useFetchData<WebRTCCode[]>('/dashboard/api/webrtc-codes', []);
  const [showCreate, setShowCreate] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<WebRTCCode | null>(null);
  const [editLabel, setEditLabel] = useState<WebRTCCode | null>(null);
  const [deleting, setDeleting] = useState(false);

  async function handleDelete() {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await fetchOrThrow(`/dashboard/api/webrtc-codes/${confirmDelete.id}`, {
        method: 'DELETE', headers: { [CSRF_HEADER_NAME]: await getCSRFToken() },
      });
      showToast('Code deleted', 'success'); refetch();
    } catch { showToast('Failed to delete', 'error'); }
    finally { setDeleting(false); setConfirmDelete(null); }
  }

  return (
    <div>
      <PageHeader>
        <PageTitle>WebRTC</PageTitle>
        <PrimaryButton onClick={() => setShowCreate(true)}><Plus size={16} /> Create Code</PrimaryButton>
      </PageHeader>

      <Card style={{ padding: 0, overflow: 'hidden' }}>
        <Table>
          <Thead><tr><th>Code</th><th>Label</th><th>Status</th><th>Created</th><th>Expires</th><th></th></tr></Thead>
          <Tbody>
            {loading ? <SkeletonRows cols={6} /> : data.length === 0 ? (
              <tr><td colSpan={6}>
                <EmptyState icon={<Phone size={40} />} title="No WebRTC codes" body="Create a code to enable browser-based calling." />
              </td></tr>
            ) : data.map((c) => (
              <tr key={c.id}>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Mono style={{ fontSize: 12 }}>{c.webrtc_code}</Mono>
                    <CopyButton value={c.webrtc_code} />
                  </div>
                </td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ color: 'var(--text-secondary)' }}>{c.label || '—'}</span>
                    <button
                      onClick={(e) => { e.stopPropagation(); setEditLabel(c); }}
                      title="Edit label"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', color: 'var(--text-muted)', display: 'inline-flex', alignItems: 'center' }}
                    >
                      <Pencil size={12} />
                    </button>
                  </div>
                </td>
                <td>
                  {c.active_session && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11, color: 'var(--accent-green)', fontWeight: 600 }}>
                      <Radio size={10} /> live
                    </span>
                  )}
                </td>
                <td style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{formatDate(c.created_at)}</td>
                <td style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{c.expires_at ? formatDate(c.expires_at) : 'Never'}</td>
                <td onClick={(e) => e.stopPropagation()}>
                  <DestructiveButton style={{ padding: '5px 10px', fontSize: 13 }} onClick={() => setConfirmDelete(c)}>
                    <Trash2 size={14} />
                  </DestructiveButton>
                </td>
              </tr>
            ))}
          </Tbody>
        </Table>
        {data.length > 0 && (
          <div style={{ padding: '16px 20px', borderTop: '1px solid var(--bg-border)', display: 'flex', flexDirection: 'column', gap: 12 }}>
            {data.map((c) => (
              <div key={c.id}>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>
                  Embed snippet for <span style={{ fontFamily: "'JetBrains Mono', monospace", color: 'var(--text-primary)' }}>{c.webrtc_code}</span>:
                </div>
                <div style={{ position: 'relative' }}>
                  <pre style={{
                    fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
                    background: 'var(--bg-base)', border: '1px solid var(--bg-border)',
                    borderRadius: 6, padding: '10px 36px 10px 12px', margin: 0,
                    color: 'var(--text-primary)', overflowX: 'auto', whiteSpace: 'pre',
                  }}>{`<script\n  src="https://app.goguava.ai/static/build/webrtc-widgets/guava-widget-audio-orb.js"\n  data-webrtc-code="${c.webrtc_code}"\n></script>`}</pre>
                  <div style={{ position: 'absolute', top: 8, right: 8 }}>
                    <CopyButton value={`<script\n  src="https://app.goguava.ai/static/build/webrtc-widgets/guava-widget-audio-orb.js"\n  data-webrtc-code="${c.webrtc_code}"\n></script>`} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        {data.length === 0 && (
          <SetupCard>
            <p>Create a WebRTC code above, then embed it on your website:</p>
            <p><code>{'<script src="https://app.goguava.ai/static/build/webrtc-widgets/guava-widget-audio-orb.js" data-webrtc-code="grtc-..."></script>'}</code></p>
          </SetupCard>
        )}
      </Card>

      {showCreate && <CreateWebRTCModal onClose={() => setShowCreate(false)} onCreated={refetch} />}
      {editLabel && (
        <EditLabelModal
          title={editLabel.webrtc_code} url={`/dashboard/api/webrtc-codes/${editLabel.id}`}
          initialLabel={editLabel.label} placeholder="e.g. Website Widget"
          onClose={() => setEditLabel(null)} onSaved={refetch}
        />
      )}
      {confirmDelete && (
        <ConfirmModal
          title="Delete WebRTC code?"
          body="This code will stop working immediately."
          confirmLabel="Delete" destructive loading={deleting}
          onConfirm={handleDelete} onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SIP Codes page
// ---------------------------------------------------------------------------

interface SipCode { id: string; sip_code: string; created_at: string | null; active_session: boolean }

export function SipCodesPage() {
  const { data, loading, refetch } = useFetchData<SipCode[]>('/dashboard/api/sip-codes', []);
  const [creating, setCreating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<SipCode | null>(null);
  const [deleting, setDeleting] = useState(false);

  async function handleCreate() {
    setCreating(true);
    try {
      await fetchOrThrow('/dashboard/api/sip-codes', {
        method: 'POST', headers: { [CSRF_HEADER_NAME]: await getCSRFToken() },
      });
      showToast('SIP code created', 'success'); refetch();
    } catch { showToast('Failed to create', 'error'); }
    finally { setCreating(false); }
  }

  async function handleDelete() {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await fetchOrThrow(`/dashboard/api/sip-codes/${confirmDelete.id}`, {
        method: 'DELETE', headers: { [CSRF_HEADER_NAME]: await getCSRFToken() },
      });
      showToast('SIP code deleted', 'success'); refetch();
    } catch { showToast('Failed to delete', 'error'); }
    finally { setDeleting(false); setConfirmDelete(null); }
  }

  return (
    <div>
      <PageHeader>
        <PageTitle>SIP</PageTitle>
        <PrimaryButton onClick={handleCreate} disabled={creating}><Plus size={16} /> {creating ? 'Creating…' : 'Create SIP Code'}</PrimaryButton>
      </PageHeader>

      <Card style={{ padding: 0, overflow: 'hidden' }}>
        <Table>
          <Thead><tr><th>SIP Code</th><th>Termination URI</th><th>Status</th><th>Created</th><th></th></tr></Thead>
          <Tbody>
            {loading ? <SkeletonRows cols={5} /> : data.length === 0 ? (
              <tr><td colSpan={5}>
                <EmptyState icon={<Phone size={40} />} title="No SIP codes" body="Create a SIP code to route calls from your SIP trunk." />
              </td></tr>
            ) : data.map((c) => (
              <tr key={c.id}>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Mono style={{ fontSize: 12 }}>{c.sip_code}</Mono>
                    <CopyButton value={c.sip_code} />
                  </div>
                </td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Mono style={{ fontSize: 12, color: 'var(--text-secondary)' }}>sip:{c.sip_code}@sip.goguava.ai</Mono>
                    <CopyButton value={`sip:${c.sip_code}@sip.goguava.ai`} />
                  </div>
                </td>
                <td>
                  {c.active_session && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11, color: 'var(--accent-green)', fontWeight: 600 }}>
                      <Radio size={10} /> live
                    </span>
                  )}
                </td>
                <td style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{formatDate(c.created_at)}</td>
                <td onClick={(e) => e.stopPropagation()}>
                  <DestructiveButton style={{ padding: '5px 10px', fontSize: 13 }} onClick={() => setConfirmDelete(c)}>
                    <Trash2 size={14} />
                  </DestructiveButton>
                </td>
              </tr>
            ))}
          </Tbody>
        </Table>
        <SetupCard>
          <p>Set your SIP trunk termination URI to: <code>sip:{'<your-sip-code>'}@sip.goguava.ai</code></p>
          <p>In your call controller: <code>call_info.call_type === "sip"</code>. Use <code>call_info.sip_headers['diversion']</code> to identify the originally dialed number when sharing a trunk across multiple numbers.</p>
        </SetupCard>
      </Card>

      {confirmDelete && (
        <ConfirmModal
          title="Delete SIP code?"
          body="This code will stop working immediately. Your SIP trunk will stop routing calls."
          confirmLabel="Delete" destructive loading={deleting}
          onConfirm={handleDelete} onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
}
